import { Link } from 'react-router-dom';
import ResourceLayout from '../../components/resources/ResourceLayout';
import useSEO from '../../hooks/useSEO';
import { canonicalUrl } from '../../utils/testSite';
import { guidePageSchema } from '../../utils/schema';

const ARTICLE_SCHEMA = {
  '@type': 'Article',
  headline: 'Arizona\'s New CE Rules: Water, Firewise & Deed Fraud',
  description: 'ADRE now requires 1 hour each of Arizona Water, Firewise, and Deed Fraud CE. What changed, who it affects, and how to satisfy it for $9.99.',
  datePublished: '2026-06-01',
  dateModified: '2026-06-10',
  author: { '@type': 'Organization', name: 'Steady Education' },
  publisher: {
    '@type': 'EducationalOrganization',
    name: 'Steady Education',
    url: 'https://steadyeducation.com',
  },
};

const RELATED = [
  { url: '/arizona-real-estate-ce-requirements', title: 'Arizona CE Requirements 2026', desc: 'The complete 24-hour breakdown by category.' },
  { url: '/how-fast-can-i-finish-arizona-ce', title: 'How Fast Can You Finish 24 Hours?', desc: 'The 9-hour daily cap and the minimum 3-day schedule.' },
  { url: '/arizona-ce-online', title: 'Can I Do All My CE Online?', desc: 'Yes. How ADRE-approved online CE actually works.' },
];

export default function Rules2025Page() {
  useSEO({
    title: "Arizona's New CE Rules: Water, Firewise & Deed Fraud",
    description: 'ADRE now requires 1 hour each of Arizona Water, Firewise, and Deed Fraud CE. What changed, who it affects, and how to satisfy it for $9.99.',
    url: canonicalUrl('/arizona-ce-2025-rule-changes'),
    jsonLd: guidePageSchema(ARTICLE_SCHEMA, {
      title: '2025 Rule Changes',
      url: canonicalUrl('/arizona-ce-2025-rule-changes'),
    }),
  });

  return (
    <ResourceLayout
      breadcrumb="2025 Rule Changes"
      title="What Changed in Arizona CE: The Water, Firewise & Deed Fraud Requirement Explained"
      subtitle="If you renewed your Arizona license before 2025, your next renewal looks different. Three new mandatory CE topics took effect January 1, 2025 — here's exactly what they are and how to satisfy them."
      relatedGuides={RELATED}
    >
      <p>Beginning <strong>January 1, 2025</strong>, ADRE added three new mandatory CE topics to every license renewal:</p>

      <ul>
        <li><strong>Arizona Water (1 hour)</strong> — water rights, supply, and what they mean for real estate transactions in a desert state</li>
        <li><strong>Firewise (1 hour)</strong> — fire safety and prevention as it relates to property and wildland-urban interface areas</li>
        <li><strong>Deed Fraud (1 hour)</strong> — identifying and preventing fraudulent transfers, a fast-growing crime in Maricopa County</li>
      </ul>

      <h2>Who must take them</h2>
      <p>Every licensee renewing on or after January 1, 2025 — salespersons, associate brokers, and designated brokers alike.</p>

      <h2>How the math changed</h2>
      <p>The six traditional 3-hour categories (Agency, Contract Law, Commissioner's Standards, Disclosure, Fair Housing, Legal Issues) still stand. The three new 1-hour topics take 3 hours that used to be electives — you now have <strong>3 elective hours instead of 6</strong>.</p>

      <h2>The common mistake</h2>
      <p>Agents who renewed in 2024 assume their usual course list still works, then hit a category deficiency when ADRE's portal rejects their renewal. The portal will show exactly which categories you're missing — <strong>check it before your renewal month, not during.</strong></p>

      <h2>The efficient fix</h2>
      <p>ADRE allows the three topics to be taken individually or combined into a single approved 3-hour course. Steady Education's <strong>"Arizona Water, Firewise &amp; Deed Fraud"</strong> course covers all three requirements in one sitting for $9.99 — or it's included in the Complete Arizona CE Package ($49.99 for all 24 hours).</p>

      <div style={inlineCta}>
        <strong>Need just the 2025 requirements?</strong> $9.99 covers all three.
        Need the full 24 hours? $49.99 covers everything.
        <div style={{ marginTop: 14 }}>
          <Link to="/arizona/courses" style={inlineCtaBtn}>Browse courses →</Link>
        </div>
      </div>

      <h2>Why these three topics?</h2>
      <p>Arizona's legislature and ADRE responded to three escalating risks: long-term water supply commitments affecting new development (Rio Verde Foothills made national news), wildfire exposure in growing exurban communities, and a surge in deed fraud targeting vacant land owners — Maricopa County now offers free title alert monitoring because of it. Knowing this material isn't just compliance; it's client protection.</p>

      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', fontStyle: 'italic', marginTop: 32 }}>
        Source: ADRE Education Division FAQ (azre.gov), effective January 1, 2025.
      </p>
    </ResourceLayout>
  );
}

const inlineCta = { background: '#FAF7F2', border: '1px solid #d4c5ad', borderRadius: 12, padding: '20px 24px', marginTop: 24 };
const inlineCtaBtn = { display: 'inline-block', background: '#2C453E', color: 'white', padding: '10px 22px', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 14 };
